import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, PlusCircle, Home, Package, Calculator, User, LogOut, Wallet, Tag, Menu, X, Truck, MessageSquare, Settings } from 'lucide-react';

const AdminLayout = ({ onLogout }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const isActive = (path) => {
        return location.pathname === path ? 'bg-purple-100 text-purple-700' : 'text-gray-600 hover:bg-gray-50';
    };

    const handleLogout = () => {
        if (window.confirm('¿Estás seguro de que quieres cerrar sesión?')) {
            onLogout();
            navigate('/admin/login');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile Header */}
            <div className="lg:hidden bg-white border-b border-gray-200 p-4 sticky top-0 z-30 flex items-center justify-between">
                <div className="flex items-center gap-2 text-purple-600">
                    <Package className="w-6 h-6" />
                    <span className="font-bold text-lg">Admin Panel</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                    <Menu className="w-6 h-6" />
                </button>
            </div>

            {/* Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 
                    transform transition-transform duration-300 ease-in-out flex flex-col
                    lg:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
            >
                <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-purple-600">
                        <Package className="w-8 h-8" />
                        <span className="text-xl font-bold">Admin Panel</span>
                    </div>
                    <button
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="lg:hidden text-gray-500 hover:bg-gray-100 rounded-lg p-1"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
                    <Link
                        to="/admin"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin')}`}
                    >
                        <LayoutDashboard className="w-5 h-5" />
                        <span className="font-medium">Dashboard</span>
                    </Link>

                    <Link
                        to="/admin/new"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/new')}`}
                    >
                        <PlusCircle className="w-5 h-5" />
                        <span className="font-medium">Nuevo Producto</span>
                    </Link>

                    <Link
                        to="/admin/bulk-price"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/bulk-price')}`}
                    >
                        <Calculator className="w-5 h-5" />
                        <span className="font-medium">Actualizar Precios</span>
                    </Link>

                    <Link
                        to="/admin/payment-methods"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/payment-methods')}`}
                    >
                        <Wallet className="w-5 h-5" />
                        <span className="font-medium">Medios de Pago</span>
                    </Link>

                    <Link
                        to="/admin/shipping"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/shipping')}`}
                    >
                        <Truck className="w-5 h-5" />
                        <span className="font-medium">Configuración de Envío</span>
                    </Link>

                    <Link
                        to="/admin/promo-config"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/promo-config')}`}
                    >
                        <Tag className="w-5 h-5" />
                        <span className="font-medium">Precio preferencial</span>
                    </Link>

                    <Link
                        to="/admin/discounts"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/discounts')}`}
                    >
                        <Tag className="w-5 h-5" />
                        <span className="font-medium">Productos en Oferta</span>
                    </Link>

                    <div className="my-2 border-t border-gray-100"></div>
                    <Link
                        to="/admin/platformUsers"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/platformUsers')}`}
                    >
                        <User className="w-5 h-5" />
                        <span className="font-medium">Usuarios</span>
                    </Link>
                    <Link
                        to="/admin/interactions"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/interactions')}`}
                    >
                        <MessageSquare className="w-5 h-5" />
                        <span className="font-medium">Interacciones</span>
                    </Link>
                    <Link
                        to="/admin/settings"
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/settings')}`}
                    >
                        <Settings className="w-5 h-5" />
                        <span className="font-medium">Configuración General</span>
                    </Link>

                    <div className="pt-4 mt-4 border-t border-gray-100">
                        <Link
                            to="/"
                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <Home className="w-5 h-5" />
                            <span className="font-medium">Ver Catálogo</span>
                        </Link>
                    </div>
                </nav>

                {/* Logout Button */}
                <div className="p-4 border-t border-gray-100">
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors w-full"
                    >
                        <LogOut className="w-5 h-5" />
                        <span className="font-medium">Cerrar Sesión</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="lg:ml-64 p-4 lg:p-8 transition-all duration-300">
                <div className="w-full max-w-[1600px] mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
